<template>
    <div
        ref="gallery"
        :class="galleryClasses"
        v-infinite-scroll="loadInfiniteScroll"
        :infinite-scroll-disabled="disableInfiniteScroll"
        :infinite-scroll-distance="200"
    >
            <slot></slot>

            <draggable :list="concatItems" :disabled="!draggable" @update="doDragUpdate" @start="doDragStart" draggable=".draggable" @end="doDragEnd">
                <transition-group name="fade" tag="div" class="row equal">
                    <slot name="extra"></slot>

                    <div class="draggable" :class="itemClasses" v-for="mediaItem in concatItems" :key="mediaItem.id">
                        <slot name="item" v-bind="{ mediaItem }">
                            <media-item class="col-md-12" :item="mediaItem" />
                        </slot>
                    </div>

                    <slot name="after"></slot>

                </transition-group>
            </draggable>

            <slot v-if="showNoItemsMsg && !hasItems && (!isLoading && !loading)" name="noitems">
                <div class="no-items-found col-md-12">{{ noItemsMsg }}</div>
            </slot>
            <spinner v-else :show="showSpinner" :message="loadingMsg" :size="45" />

            <div v-show="!disableInfiniteScroll" class="scroll-message">
                <div class="scroll-down">
                    <i class="fas fa-arrow-to-bottom"></i>
                    <span>Scroll down to load more</span>
                    <i class="fas fa-arrow-to-bottom"></i>
                </div>
            </div>
        </div>
</template>

<script>
import Vue from 'vue'; // TODO: infitite scroll global?
import { isObject } from 'lodash';

import draggable from 'vuedraggable';
import infiniteScroll from 'vue-infinite-scroll';
import MediaItem from '@/components/media/MediaItem';
import Spinner from '@/components/media/Spinner';

Vue.use(infiniteScroll);

export default {
    name: 'Gallery',
    inheritAttrs: false,
    components: {
        MediaItem,
        Spinner,
    },
    props: {
        items: {
            type: Array,
            required: true,
        },
        meta: {
            type: Object,
        },
        params: {
            type: Object,
        },
        draggable: {
            type: Boolean,
            default: false,
        },
        itemClasses: { // quick hack so can have differnt grid sizes TODO: flexbox!
            type: String,
            default: 'media-item-container col-xs-12 col-sm-6 col-md-4 col-lg-3'
        },
        fetchMethod: {
            type: String,
        },
        padBottom: {
            type: Boolean,
            default: true,
        },
        noItemsMsg: {
            type: String,
            default: 'No items found.',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        loadingMsg: {
            type: String,
        },
        showNoItemsMsg: {
            type: Boolean,
            default: true,
        },
        customClass: {
            type: String,
        },
    },
    data() {
        return {
            isLoading: false,
            concatItems: [],
            metaData: this.meta,
        }
    },
    computed: {
        galleryClasses() {
            const classes = {
                gallery: true,
                'is-loading': this.isLoading,
                'has-items': Array.isArray(this.concatItems) && this.concatItems.length > 0,
                'no-items': Array.isArray(this.concatItems) && this.concatItems.length === 0 && !this.isLoading,
                'has-bottom-padding': this.padBottom,
            };

            if (this.customClass) classes[this.customClass] = true;
            return classes;
        },
        isLastPage() {
            return this.metaData && this.metaData.current_page >= this.metaData.last_page;
        },
        hasPage() {
            return this.metaData && this.metaData.hasOwnProperty('current_page') && this.metaData.current_page;
        },
        disableInfiniteScroll() {
            return (!this.hasPage || this.isLastPage || this.isLoading || !this.fetchMethod);
        },
        showSpinner() {
            return this.loading || this.isLoading;
        },
        hasItems() {
            return this.concatItems && Array.isArray(this.concatItems) && this.concatItems.length > 0;
        },
    },
    watch: {
        items: {
            immediate: true,
            handler(newValue, oldValue) {
                if (Array.isArray(newValue) && newValue.length > 0) {
                    this.concatItems = newValue.filter(i => isObject(i));
                } else {
                    this.concatItems = [];
                }
            },
        },
        meta: {
            immediate: true,
            handler(newValue) {
                this.metaData = newValue;
            },
        },
    },
    methods: {
        doDragStart(evt, originalEvent) {
            this.$emit('drag-start', evt);
        },
        doDragEnd(evt, originalEvent) {
            this.$emit('drag-end', evt);
        },
        doDragUpdate(evt, originalEvent) {
            this.$emit('drag-update', evt);
        },
        loadInfiniteScroll() {
            if (this.disableInfiniteScroll) return;

            this.params.page = this.metaData.current_page + 1;
            this.isLoading = true;

            this.$store.dispatch(this.fetchMethod, this.params).then((response) => {
                let uniqueItems = this.concatItems.filter((item) => {
                    return !response.data.filter((newItem) => {
                        return item.id === newItem.id;
                    }).length;
                });

                this.concatItems = uniqueItems.concat(response.data);
                this.metaData = response.meta;
                this.isLoading = false;
                this.$emit('items-loaded', this.concatItems);
            });
        }
    }
}
</script>

<style lang="scss" scoped>
.gallery {
    position: relative;
    width: 100%;
    padding-top: 0;

    .no-items-found {
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 400;
        width: 100%;
    }

    .scroll-message {
        position:absolute;
        height: 80px;
        width:100%;
        bottom: 20px;
        text-align: center;
    }
    &-item {
        margin-bottom: 10px;
    }

    @media (min-width: 992px) {
        //min-height: calc(100vh - 115px);
        &.has-padding-bottom {
            padding-bottom: 150px;
        }
    }
}

.gallery.is-loading {
    padding-bottom: 0;
    min-height: calc(100vh - 185px);
}

/* Fix for uneven heights */
.row.equal {
    display: flex;
    flex-wrap: wrap;

    div[class^="col-"] {
        display: flex;
    }

    .gallery-item {
        flex-grow: 1;
    }
}

.scroll-down {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;

    font-weight: 600;
    font-size: 2rem;
    opacity: 0.4;

    i {
        font-weight: normal;
    }
}

.alert {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.alert .glyphicon {
    font-size: 36px;
    margin-right: 8px;
    align-self: center; // if you want it centered vertically
}

/* base animations for gallery items */

.fade {
    backface-visibility: hidden;
    z-index: 1;
}

/* moving */
.fade-move {
    transition: all 600ms ease-in-out 50ms;
}

/* appearing */
.fade-enter-active {
    transition: all 400ms ease-out;
}

/* disappearing */
.fade-leave-active {
    transition: all 200ms ease-in;
    position: absolute;
    z-index: 0;
}

/* appear at / disappear to */
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
